import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const VinInputSection = ({ onVinSubmit, isLoading, error }) => {
  const [vin, setVin] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const validateVin = (value) => {
    // Remove spaces and convert to uppercase
    const cleanVin = value?.replace(/\s/g, '')?.toUpperCase();
    
    // Check length
    if (cleanVin?.length !== 17) return false;
    
    // Check for invalid characters (I, O, Q)
    if (/[IOQ]/?.test(cleanVin)) return false;
    
    // Check if alphanumeric
    if (!/^[A-HJ-NPR-Z0-9]{17}$/?.test(cleanVin)) return false;
    
    return true;
  };

  const formatVin = (value) => {
    // Remove all non-alphanumeric characters and convert to uppercase
    const cleaned = value?.replace(/[^A-HJ-NPR-Z0-9]/gi, '')?.toUpperCase();
    
    // Limit to 17 characters
    const limited = cleaned?.slice(0, 17);
    
    // Add spaces for readability (groups of 3-2-6-6)
    let formatted = '';
    for (let i = 0; i < limited?.length; i++) {
      if (i === 3 || i === 5 || i === 11) {
        formatted += ' ';
      }
      formatted += limited?.[i];
    }
    
    return formatted;
  };

  const handleVinChange = (e) => {
    const formattedValue = formatVin(e?.target?.value);
    setVin(formattedValue);
    
    const cleanVin = formattedValue?.replace(/\s/g, '');
    setIsValid(validateVin(cleanVin));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (isValid && !isLoading) {
      const cleanVin = vin?.replace(/\s/g, '');
      onVinSubmit(cleanVin);
    }
  };

  const handleClear = () => {
    setVin('');
    setIsValid(false);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
            <Icon name="Search" size={20} color="white" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Consulta de VIN</h2>
            <p className="text-sm text-muted-foreground">Ingrese el número VIN de 17 caracteres</p>
          </div>
        </div>
        
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            iconName="HelpCircle"
            iconSize={16}
            className="text-muted-foreground hover:text-foreground"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          />
          
          {showTooltip && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-popover border border-border rounded-md shadow-elevation-lg p-4 z-10">
              <h4 className="font-medium text-popover-foreground mb-2">Formato de VIN</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Exactamente 17 caracteres alfanuméricos</li>
                <li>• No se permiten las letras I, O, Q</li>
                <li>• Ejemplo: 1HGBH41JXMN109186</li>
                <li>• Se formatean automáticamente con espacios</li>
              </ul>
            </div>
          )}
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <Input
            label="Número VIN"
            type="text"
            value={vin}
            onChange={handleVinChange}
            placeholder="Ej: 1HG BH4 1JXMN1 09186"
            error={error}
            className="font-mono text-lg tracking-wider"
            maxLength={20} // 17 characters + 3 spaces
          />
          
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center space-x-4">
              <span className={`text-sm ${vin?.replace(/\s/g, '')?.length === 17 ? 'text-success' : 'text-muted-foreground'}`}>
                {vin?.replace(/\s/g, '')?.length}/17 caracteres
              </span>
              
              {isValid && (
                <div className="flex items-center space-x-1 text-success">
                  <Icon name="CheckCircle" size={14} />
                  <span className="text-sm">VIN válido</span>
                </div>
              )}
            </div>
            
            {vin && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClear}
                iconName="X"
                iconSize={14}
                className="text-muted-foreground hover:text-foreground"
              >
                Limpiar
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            type="submit"
            variant="default"
            disabled={!isValid || isLoading}
            loading={isLoading}
            iconName="Search"
            iconPosition="left"
            iconSize={16}
            className="flex-1 sm:flex-none"
          >
            {isLoading ? 'Decodificando...' : 'Decodificar VIN'}
          </Button>
          
          <div className="hidden sm:flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={14} />
            <span>Consultas restantes hoy: 47/50</span>
          </div>
        </div>
      </form>
      {/* Quick Examples */}
      <div className="mt-6 pt-4 border-t border-border">
        <p className="text-sm text-muted-foreground mb-2">Ejemplos de VIN válidos:</p>
        <div className="flex flex-wrap gap-2">
          {['1HGBH41JXMN109186', '2T1BURHE0JC014721', '5NPE34AF4HH012345']?.map((exampleVin) => (
            <Button
              key={exampleVin}
              variant="outline"
              size="sm"
              onClick={() => {
                let formatted = formatVin(exampleVin);
                setVin(formatted);
                setIsValid(true);
              }}
              className="font-mono text-xs"
            >
              {exampleVin}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VinInputSection;